/**
 * Componente presentacional para mostrar detalles de ruta y sticker
 * Lee delivery/sticker de state.order (cargados por el componente padre)
 * Carga bultos (packages) ya que el padre no los carga. Con envío por Asigna o MSoft
 * ofrece descargar la nota de entrega de la agencia
 */
import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Link,
    Stack,
    Chip,
    Divider
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import PhoneIcon from "@mui/icons-material/Phone";
import { BADGE_COLORS, getBadgeStyles } from "../../../../components/ui/badgeColors";
import TagIcon from "@mui/icons-material/Tag";
import InfoField from "./InfoField";
import { StateContext } from "../../../../components/context/StateProvider";
import DeliveryService from "../../../../services/delivery.service";
import Utils from "../../../../utils/Utils";
import Toasts from "../../../../components/notificacion/Toasts";
import AsignaService from "../../../../services/asigna.service";
import { IMAGE_TYPE } from "../../../../config/constants";
import { descargarBase64 } from "../../utils/descargas";

// Enlaces del bloque de seguimiento (agencia y nota de entrega)
const enlaceSx = {
    fontSize: 13,
    fontWeight: 600,
    color: "#cc071e",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    "&:hover": { color: "#a30618", textDecoration: "underline" },
};

const DetallesRuta = ({ orderNum, year }) => {
    const { state, updateState } = useContext(StateContext);
    const [cargandoNota, setCargandoNota] = useState(false);

    // Cargar bultos (packages) - delivery y sticker ya vienen de state.order
    useEffect(() => {
        const cargarBultos = async () => {
            // Solo cargar si hay delivery y no están ya cargados
            if (state.order.delivery?.id && !state.order.packages) {
                try {
                    const packages = await DeliveryService.getBultos(
                        state.order.delivery.id,
                        orderNum,
                        year
                    );

                    let registerDate = null;
                    packages.forEach((pack) => {
                        if (pack.registrado !== null && (registerDate === null || new Date(pack.registrado) > registerDate)) {
                            registerDate = new Date(pack.registrado);
                        }
                    });

                    // Solo actualizar packages y lastRegisterPackage
                    updateState({
                        order: {
                            ...state.order,
                            packages: packages || [],
                            lastRegisterPackage: registerDate
                        }
                    });
                } catch (error) {
                    console.error('Error al cargar bultos:', error);
                }
            }
        };

        cargarBultos();
    }, [state.order.delivery?.id, orderNum, year]);

    const delivery = state.order.delivery;
    const sticker = state.order.sticker;
    const packages = state.order.packages || [];
    const lastRegisterPackage = state.order.lastRegisterPackage;

    if (!delivery || !sticker) {
        return null;
    }

    const expedicionConsolidada = packages.length === sticker.packages;

    // Descarga la nota de entrega (imagen o PDF) que guarda la agencia del envío
    const handleVerNota = async () => {
        const avisarError = (mensaje) =>
            updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, "error")] });

        setCargandoNota(true);
        try {
            const nota = await AsignaService.getNote(sticker, delivery);
            if (!nota?.data) {
                avisarError("No se pudo obtener la nota de entrega: " + (nota?.message || "la agencia no la ha devuelto"));
                return;
            }
            const tipo = IMAGE_TYPE[nota.format] ?? IMAGE_TYPE.PDF;
            descargarBase64(nota.data, tipo.MIME, `nota-${sticker.serie}-${sticker.order}.${tipo.EXT}`);
        } catch (error) {
            avisarError("No se pudo obtener la nota de entrega. Inténtalo de nuevo.");
        } finally {
            setCargandoNota(false);
        }
    };

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
                "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }
            }}
        >
            {/* Header con nombre de ruta */}
            <Box
                sx={{
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e9ecef",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
                        }}
                    >
                        <LocalShippingIcon sx={{ fontSize: 22, color: "#cc071e" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#6c757d",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                mb: 0.25
                            }}
                        >
                            Ruta de entrega
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#212529",
                                lineHeight: 1.2
                            }}
                        >
                            {delivery.name || '-'}
                        </Typography>
                    </Box>
                    {expedicionConsolidada && (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            label="Consolidada"
                            size="small"
                            sx={{
                                ...getBadgeStyles(BADGE_COLORS.GREEN, { height: 28, fontSize: 12 }),
                                "& .MuiChip-icon": { color: BADGE_COLORS.GREEN.text },
                            }}
                        />
                    )}
                </Stack>
            </Box>

            {/* Contenido principal */}
            <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack spacing={2.5} sx={{ flex: 1 }}>
                    {/* Identificadores */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#495057",
                                mb: 1.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                            }}
                        >
                            Identificación
                        </Typography>
                        <Stack spacing={2}>
                            <InfoField icon={TagIcon} label="ID Ruta">
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#cc071e" }}>
                                    #{delivery.id || '-'}
                                </Typography>
                            </InfoField>
                            <InfoField icon={QrCodeIcon} label="ID Sage">
                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                    {delivery.counter || '-'}
                                </Typography>
                            </InfoField>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Detalles de envío */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#495057",
                                mb: 1.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                            }}
                        >
                            Detalles de envío
                        </Typography>
                        <Stack spacing={2}>
                            <InfoField icon={CalendarTodayIcon} label="Fecha">
                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                    {delivery.date ? Utils.formatDateWithoutHour(Date.parse(delivery.date)) : '-'}
                                </Typography>
                            </InfoField>

                            <InfoField icon={BusinessIcon} label="Agencia">
                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                    {sticker.nameAgency || '-'}
                                </Typography>
                            </InfoField>

                            <InfoField icon={InventoryIcon} label="Bultos">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#212529" }}>
                                        {sticker.packages || '0'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                                        {expedicionConsolidada ? 'completados' : 'pendientes'}
                                    </Typography>
                                </Box>
                            </InfoField>
                        </Stack>
                    </Box>

                    {/* Dirección de envío */}
                    {(sticker.name || sticker.address || sticker.cp || sticker.county) && (
                        <>
                            <Divider />
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#495057",
                                        mb: 1.5,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    Dirección de envío
                                </Typography>
                                <Stack spacing={2}>
                                    {sticker.name && (
                                        <InfoField icon={PersonIcon} label="Destinatario">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {sticker.name}
                                            </Typography>
                                        </InfoField>
                                    )}

                                    {sticker.address && (
                                        <InfoField icon={HomeIcon} label="Dirección">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {sticker.address}
                                            </Typography>
                                        </InfoField>
                                    )}

                                    {(sticker.cp || sticker.county) && (
                                        <InfoField icon={LocationOnIcon} label="Localidad">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {[sticker.cp, sticker.county].filter(Boolean).join(' - ')}
                                            </Typography>
                                        </InfoField>
                                    )}

                                    {sticker.state && (
                                        <InfoField icon={PublicIcon} label="Provincia">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {sticker.state}
                                            </Typography>
                                        </InfoField>
                                    )}

                                    {sticker.phone && (
                                        <InfoField icon={PhoneIcon} label="Teléfono">
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                {sticker.phone}
                                            </Typography>
                                        </InfoField>
                                    )}
                                </Stack>
                            </Box>
                        </>
                    )}

                    {/* Estado de consolidación */}
                    {lastRegisterPackage && (
                        <>
                            <Divider />
                            <Box>
                                <InfoField icon={CheckCircleIcon} label="Último bulto consolidado">
                                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#495057" }}>
                                        {Utils.formatDate(lastRegisterPackage)}
                                    </Typography>
                                </InfoField>
                            </Box>
                        </>
                    )}

                    {/* Número de seguimiento */}
                    {sticker.expedicionAsigna && (delivery.asigna || delivery.msoft) && (
                        <>
                            <Divider />
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: 1.5,
                                    border: "1px solid #e9ecef"
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#6c757d",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        mb: 1
                                    }}
                                >
                                    Número de seguimiento
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: "#212529",
                                        mb: 1.5,
                                        fontFamily: "monospace",
                                        letterSpacing: "1px"
                                    }}
                                >
                                    {sticker.expedicionAsigna}
                                </Typography>
                                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                    {delivery.asigna && (
                                        <Link
                                            href={window.env.ASIGNA_URL + sticker.expedicionAsigna}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={enlaceSx}
                                        >
                                            Ver en Asigna →
                                        </Link>
                                    )}
                                    {delivery.msoft && (
                                        <Link
                                            href={`${window.env.MSOFT_URL}?pedido=${encodeURIComponent(sticker.expedicionAsigna)}&ejercicio=${year}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={enlaceSx}
                                        >
                                            Ver en United Express →
                                        </Link>
                                    )}
                                    <Link
                                        href="#"
                                        onClick={(evento) => {
                                            evento.preventDefault();
                                            if (!cargandoNota) handleVerNota();
                                        }}
                                        aria-disabled={cargandoNota}
                                        sx={{ ...enlaceSx, ...(cargandoNota && { color: "#6c757d", pointerEvents: "none" }) }}
                                    >
                                        {cargandoNota ? "Obteniendo nota..." : "Ver nota de entrega →"}
                                    </Link>
                                </Stack>
                            </Box>
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export default DetallesRuta;
